import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useMemberMutations, useTeamMutations, useTeams } from "@/hooks";
import { ApiError } from "@/services";
import type { InviteResult } from "@/types/api";
import { useEffect, useState } from "react";

/**
 * The value that means "no team".
 *
 * A sentinel rather than "": Radix reads an empty value as "nothing selected"
 * and shows the placeholder, so an empty-valued item could be chosen and then
 * look like it had not been. This has to be a value the option can actually
 * hold.
 */
const NO_TEAM = "__none__";

/**
 * Confirm an invite, choosing a team PER PERSON.
 *
 * WHY A STEP AT ALL, when the addresses are already typed. Assigning a team is
 * only possible while you still know who you just invited. Sent as a bare
 * invite, the admin has to go to Teams, find each new name in a roster that now
 * includes them, and add them one at a time — for exactly the people they had
 * in their hand a moment ago. This is the one moment the information is free.
 *
 * PER PERSON, not one team for the batch: a pasted list is usually a hire round
 * across several teams, and forcing one choice would mean inviting three times
 * or fixing it afterwards. "Set all to…" is offered above the list because the
 * primary motion here is pasting a spreadsheet column, and setting fifteen
 * dropdowns by hand to the same value is not a feature.
 *
 * A team is OPTIONAL everywhere. Inviting must never depend on having decided
 * where someone belongs, and an org with no teams yet must still onboard people.
 *
 * The invite happens FIRST and stands on its own. If an assignment then fails,
 * that person is still invited, and the PAGE reports it alongside every other
 * per-email outcome. This dialog acts and hands back; it does not stay open to
 * explain itself. It used to, and when some addresses were also over capacity
 * the buy-seats dialog opened on top of it, hiding the failure behind the one
 * asking for money.
 */
export function InviteDialog({
	open,
	onOpenChange,
	emails,
	onInvited,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Already parsed and de-duplicated by the caller. */
	emails: string[];
	/**
	 * The invite result, plus any addresses that were invited but could NOT be
	 * put on their chosen team. The caller owns what happens next — the
	 * over-capacity flow offers to buy seats, which is not this dialog's
	 * business, and it reports outcomes in one place.
	 */
	onInvited: (result: InviteResult, notAssigned: string[]) => void;
}) {
	const { invite } = useMemberMutations();
	const { addMember } = useTeamMutations();
	const { data: teamsData } = useTeams();
	/** email (lowercased) → team id. Absent means no team. */
	const [choice, setChoice] = useState<Record<string, string>>({});
	const [error, setError] = useState<string | null>(null);
	const [assigning, setAssigning] = useState(false);

	const teams = teamsData?.teams ?? [];
	const pending = invite.isPending || assigning;

	useEffect(() => {
		if (!open) return;
		setChoice({});
		setError(null);
	}, [open]);

	// Keyed on the lowercased address because that is how the Gateway matches a
	// seat (idx_org_member_org_email_lower), so "Ada@x.com" typed here and
	// "ada@x.com" returned by the invite are the same person.
	const key = (email: string) => email.toLowerCase();
	const setAll = (teamId: string) =>
		setChoice(
			teamId === NO_TEAM
				? {}
				: Object.fromEntries(emails.map((e) => [key(e), teamId])),
		);

	async function submit() {
		setError(null);
		let result: InviteResult;
		try {
			result = await invite.mutateAsync({ emails });
		} catch (e) {
			setError(e instanceof ApiError ? e.message : "Could not send invites.");
			return;
		}

		// Only the people who actually got a seat can be put on a team. The rest
		// were already members, invalid, or over capacity — assigning those would
		// fail one at a time for reasons the admin already has in the summary.
		const failed: string[] = [];
		// flatMap rather than map+filter: `filter` does not narrow the optional
		// away, so the pair would still be typed as possibly-undefined.
		const toAssign = result.invited.flatMap((member) => {
			const teamId = choice[key(member.email)];
			return teamId ? [{ member, teamId }] : [];
		});

		if (toAssign.length > 0) {
			setAssigning(true);
			for (const { member, teamId } of toAssign) {
				try {
					await addMember.mutateAsync({
						teamId,
						member: { memberId: member.id },
					});
				} catch {
					// Named rather than counted upstream: the admin needs to know
					// WHO to fix, and they are invited either way.
					failed.push(member.email);
				}
			}
			setAssigning(false);
		}

		onInvited(result, failed);
		onOpenChange(false);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						Invite {emails.length} {emails.length === 1 ? "person" : "people"}
					</DialogTitle>
					<DialogDescription>
						They'll get a seat the first time they sign in with that address.
						Choosing a team is optional.
					</DialogDescription>
				</DialogHeader>

				{teams.length === 0 ? (
					// Not an error and not a blocker — just the reason there is no
					// dropdown, said once rather than fifteen times.
					<p className="text-caption text-muted-foreground">
						No teams yet. You can create one later and add people then.
					</p>
				) : emails.length > 1 ? (
					<div className="flex items-center gap-3 border-b border-divider pb-4">
						<span className="shrink-0 text-caption text-muted-foreground">
							Set all to
						</span>
						<Select onValueChange={setAll} disabled={pending}>
							<SelectTrigger
								className="h-8"
								aria-label="Set every invite to one team"
							>
								<SelectValue placeholder="Choose a team…" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={NO_TEAM}>No team</SelectItem>
								{teams.map((team) => (
									<SelectItem key={team.id} value={team.id}>
										{team.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				) : null}

				{/* gap-4 rather than the dialog's own rhythm: a column of dropdowns
				    packed tight reads as one control with several rows, and the
				    point here is that each line is a separate decision. */}
				<div className="flex max-h-[45vh] flex-col gap-4 overflow-y-auto py-1">
					{emails.map((email) => (
						<div key={email} className="flex items-center gap-4">
							<span
								className="min-w-0 flex-1 truncate text-caption text-heading"
								title={email}
							>
								{email}
							</span>
							{teams.length > 0 ? (
								<Select
									value={choice[key(email)] ?? NO_TEAM}
									disabled={pending}
									onValueChange={(v) =>
										setChoice((prev) => {
											const next = { ...prev };
											if (v === NO_TEAM) delete next[key(email)];
											else next[key(email)] = v;
											return next;
										})
									}
								>
									<SelectTrigger
										className="h-8 w-44 shrink-0"
										aria-label={`Team for ${email}`}
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value={NO_TEAM}>No team</SelectItem>
										{teams.map((team) => (
											<SelectItem key={team.id} value={team.id}>
												{team.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							) : null}
						</div>
					))}
				</div>

				{error ? <p className="text-caption text-error">{error}</p> : null}

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={pending}
					>
						Cancel
					</Button>
					<Button onClick={submit} disabled={pending || emails.length === 0}>
						{invite.isPending
							? "Inviting…"
							: assigning
								? "Adding to teams…"
								: "Invite"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
